import { useEffect, useState } from 'react';
import { numero, t } from '../../i18n/idioma';
import type { WorldStatus } from '../../world/types';
import {
  FADIGA_AMBAR, FADIGA_CACADA, FADIGA_COLETA, FADIGA_ESGOTADO, FADIGA_REPOUSO, FADIGA_TETO, FADIGA_TREINO, FADIGA_VIGILIA,
  fadigaPct, taxaFadiga
} from '../../world/fadiga';
import { IconeHud } from './icones';
import { MenuPop } from './MenuPop';

/**
 * O resumo da fadiga (Renato, 29/10/2026; docs/prototipos/fadiga-resumo-prototipo.html): o pop que abre no clique do
 * anel da stamina, no topo. Mostra o que o personagem está fazendo agora (gasta ou recupera, e em quanto tempo chega a
 * 0% ou a 100%), o que gasta, o que recupera e o que acontece em 0%. Só no PC e no tablet: no celular o anel não está
 * no topo, e a Wiki explica.
 */

/** A largura do pop (o CSS usa a mesma): o WorldScreen encosta o pop no anel sem passar da borda. */
export const LARGURA_POP_FADIGA = 330;

const ICONE_DA_FAIXA: Readonly<Record<number, string>> = {
  [FADIGA_CACADA]: 'cacar',
  [FADIGA_COLETA]: 'oficios',
  [FADIGA_TREINO]: 'treinar',
  [FADIGA_VIGILIA]: 'cidade'
};

function nomeDaFaixa(faixa: number): string {
  if (faixa === FADIGA_CACADA) return t('fadiga.resumo.faixa.cacada');
  if (faixa === FADIGA_COLETA) return t('fadiga.resumo.faixa.coleta');
  if (faixa === FADIGA_TREINO) return t('fadiga.resumo.faixa.treino');
  if (faixa === FADIGA_VIGILIA) return t('fadiga.resumo.faixa.vigilia');
  return t('fadiga.resumo.faixa.repouso');
}

/** "+10%/h", "−12,5%/h", "~+7%/h": a taxa da faixa em % da barra por hora. */
export function porHora(taxaPorMin: number): string {
  const n = Math.abs(taxaPorMin) * 60 / FADIGA_TETO * 100;
  const uma = Math.round(n * 10) / 10;
  const txt = Math.abs(n - uma) > 0.01 ? `~${numero(Math.round(n))}` : numero(uma);
  const sinal = taxaPorMin < 0 ? '−' : '+';
  return txt.startsWith('~') ? `~${sinal}${txt.slice(1)}%/h` : `${sinal}${txt}%/h`;
}

function duracao(min: number): string {
  const total = Math.max(1, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return t('fadiga.resumo.dur.m', { m });
  if (m === 0) return t('fadiga.resumo.dur.h', { h });
  return t('fadiga.resumo.dur.hm', { h, m: String(m).padStart(2, '0') });
}

type Tom = '' | 'is-ambar' | 'is-esgotado';

function tomDe(valor: number): Tom {
  if (valor <= FADIGA_ESGOTADO) return 'is-esgotado';
  if (valor <= FADIGA_AMBAR) return 'is-ambar';
  return '';
}

export function PopFadiga({ x, status, onFechar }: { x: number; status: WorldStatus | null; onFechar(): void }) {
  // O status chega com a fadiga já interpolada; o relógio só refaz as contas de tempo com o pop parado na tela.
  const [, setTique] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTique(n => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const valor = status?.fadiga ?? FADIGA_TETO;
  const faixa = status?.fadigaFaixa ?? FADIGA_VIGILIA;
  const pct = fadigaPct(valor);
  const tom = tomDe(valor);
  const taxa = taxaFadiga(faixa);
  const gasta = taxa < 0;

  let meta: string;
  if (gasta) {
    if (valor <= FADIGA_ESGOTADO)
      meta = t(faixa === FADIGA_COLETA ? 'fadiga.resumo.coletaAcabou' : 'fadiga.resumo.esgotadaCacando');
    else meta = t('fadiga.resumo.zeraEm', { tempo: duracao((valor - FADIGA_ESGOTADO) / -taxa) });
  } else if (valor >= FADIGA_TETO) meta = t('fadiga.resumo.cheia');
  else meta = t('fadiga.resumo.encheEm', { tempo: duracao((FADIGA_TETO - valor) / taxa) });

  const linha = (alvo: number, nome: string, valorTxt: string, cls: string) => (
    <div className={faixa === alvo ? `${cls} is-agora` : cls} aria-current={faixa === alvo || undefined}>
      <span>{nome}</span><b>{valorTxt}</b>
    </div>
  );

  return (
    <MenuPop id="fadiga" titulo={t('fadiga.resumo.titulo')} x={x} onFechar={onFechar}>
      <div className="fd-cab">
        <span className={`fd-pct ${tom}`}>{numero(pct)}%</span>
        <span className="fd-tit"><b>{t('fadiga.resumo.titulo')}</b><small>{t('fadiga.resumo.sub')}</small></span>
      </div>
      <div className={`fd-barra ${tom}${pct > 100 ? ' is-excede' : ''}`} aria-hidden="true"><i style={{ width: `${Math.min(100, pct)}%` }} /><b /></div>

      <div className="mkt-ci fd-agora">
        <span className="itemicon" style={{ width: 28, height: 28 }}>
          <IconeHud nome={ICONE_DA_FAIXA[faixa] ?? 'cidade'} className="ic-linha" />
        </span>
        <span>
          <span className="mkt-ci__n">{t('fadiga.resumo.agora', { faixa: nomeDaFaixa(faixa) })}</span>
          <span className="mkt-ci__m">{meta}</span>
        </span>
        <span className="mkt-ci__p"><b className={gasta ? 'is-gasta' : 'is-recupera'}>{porHora(taxa)}</b></span>
      </div>

      {tom === 'is-esgotado' && <p className="fd-alerta is-esgotado" role="status">{t('fadiga.resumo.alertaZero')}</p>}
      {tom === 'is-ambar' && <p className="fd-alerta is-ambar" role="status">{t('fadiga.resumo.alertaAmbar')}</p>}

      <div className="fd-duas">
        <div>
          <h3>{t('fadiga.resumo.gasta')}</h3>
          <div className="fd-linhas">
            {linha(FADIGA_CACADA, t('fadiga.resumo.faixa.cacada'), porHora(taxaFadiga(FADIGA_CACADA)), 'is-gasta')}
            {linha(FADIGA_COLETA, t('fadiga.resumo.faixa.coleta'), porHora(taxaFadiga(FADIGA_COLETA)), 'is-gasta')}
          </div>
        </div>
        <div>
          <h3>{t('fadiga.resumo.recuperaEm')}</h3>
          <div className="fd-linhas">
            {linha(FADIGA_TREINO, t('fadiga.resumo.manequim'), t('fadiga.resumo.dur.h', { h: 8 }), 'is-recupera')}
            {linha(FADIGA_VIGILIA, t('fadiga.resumo.naCidade'), t('fadiga.resumo.dur.h', { h: 10 }), 'is-recupera')}
            {linha(FADIGA_REPOUSO, t('fadiga.resumo.faixa.repouso'), t('fadiga.resumo.dur.h', { h: 14 }), 'is-recupera')}
          </div>
        </div>
      </div>

      <div>
        <h3>{t('fadiga.resumo.emZero')}</h3>
        <ul className="fd-zero">
          <li>{t('fadiga.resumo.zero.saque')}</li>
          <li>{t('fadiga.resumo.zero.skill')}</li>
          <li>{t('fadiga.resumo.zero.coleta')}</li>
          <li>{t('fadiga.resumo.zero.cacada')}</li>
        </ul>
      </div>

      <p className="p-sub">
        <span>{t('fadiga.resumo.naoMexe')}</span>
        {/* A Wiki é do site: abre em outra aba, sem fechar o jogo (como em Mais › Wiki). */}
        <a href="/wiki/sistemas/fadiga" target="_blank" rel="noopener noreferrer">{t('fadiga.resumo.wiki')}</a>
      </p>
    </MenuPop>
  );
}
